import cn from 'classnames'

import { Translation } from 'cng-common/features/translations'
import { getQualityBand } from 'partner360/components/qualityIndicator'

import {
  EMPTY_COCKPIT_VALUE,
  formatCockpitMoney,
  type ICockpitInitiativeHierarchyRow,
  type TCockpitInitiativeHierarchyQuality,
} from '../../../../../lib'
import { CockpitPlanQuality } from '../../../../cockpitPlanQuality/CockpitPlanQuality'
import { CockpitTableColumnTitle } from '../../../../components/cockpitTableTitle'
import {
  type ICockpitInitiativeHierarchyColumn,
  renderHierarchyValue,
} from '../../cockpitInitiativesHierarchy'
import { TRANSLATION_KEYS } from '../translations'

import styles from './getCockpitInitiativesQualityHierarchyColumns.module.scss'

interface IParams {
  /** Resolves a row's quality on first ask, so only the drawn and exported rows are calculated. */
  getQuality: (item: ICockpitInitiativeHierarchyRow) => TCockpitInitiativeHierarchyQuality
  currency: string
  isExpandable: (rowId: string) => boolean
}

const renderGroupHeader = (title: string) => (
  <CockpitTableColumnTitle group>
    <Translation text={title} span />
  </CockpitTableColumnTitle>
)

const renderColumnHeader = (title: string) => (
  <CockpitTableColumnTitle>
    <Translation text={title} span />
  </CockpitTableColumnTitle>
)

export const getCockpitInitiativesQualityHierarchyColumns = ({
  getQuality,
  currency,
  isExpandable,
}: IParams): ICockpitInitiativeHierarchyColumn[] => {
  const getCellValue = (
    item: ICockpitInitiativeHierarchyRow,
    getValue: (quality: TCockpitInitiativeHierarchyQuality) => number | null
  ) => getValue(getQuality(item))
  const groupColumn = (field: string, title: string): ICockpitInitiativeHierarchyColumn => ({
    field,
    title,
    className: cn(styles.groupHeader, styles.groupStart, styles.parentHeader),
    hideSettings: true,
    disableSort: true,
    titleRender: () => renderGroupHeader(title),
  })
  const valueColumn = ({
    field,
    parentField,
    title,
    first = false,
    getValue,
    format,
    isScore = false,
  }: {
    field: string
    parentField: string
    title: string
    first?: boolean
    getValue: (quality: TCockpitInitiativeHierarchyQuality) => number | null
    /** The text the cell reads — on screen and in the export alike. */
    format: (value: number | null) => string
    /** Drawn as a quality badge where there is a score to draw. */
    isScore?: boolean
  }): ICockpitInitiativeHierarchyColumn => ({
    field,
    parentField,
    title,
    className: cn(styles.valueCell, { [styles.groupStart]: first }),
    isNumeric: true,
    hideSettings: true,
    disableSort: true,
    titleRender: () => renderColumnHeader(title),
    renderFunction: (item) => {
      const value = getCellValue(item, getValue)

      return isScore && value !== null ? (
        <CockpitPlanQuality quality={value} />
      ) : (
        renderHierarchyValue(item, format(value), isExpandable(item.rowId))
      )
    },
    exportCell: (item) => {
      const value = getCellValue(item, getValue)

      return {
        text: format(value),
        value,
        badge: isScore && value !== null ? getQualityBand(value) : undefined,
      }
    },
  })
  const money = (value: number | null) => formatCockpitMoney(value, currency)
  const count = (value: number | null) => (value === null ? EMPTY_COCKPIT_VALUE : String(value))

  return [
    groupColumn('financialCoverageGroup', TRANSLATION_KEYS.financialPlanCoverage),
    valueColumn({
      field: 'financialIncremental',
      parentField: 'financialCoverageGroup',
      title: TRANSLATION_KEYS.financialIncremental,
      first: true,
      getValue: ({ financialPlanCoverage }) => financialPlanCoverage.incrementalPlanSum,
      format: money,
    }),
    valueColumn({
      field: 'initiativeIncrementalPlan',
      parentField: 'financialCoverageGroup',
      title: TRANSLATION_KEYS.initiativeIncrementalPlan,
      getValue: ({ financialPlanCoverage }) => financialPlanCoverage.incrementalInitiativesSum,
      format: money,
    }),
    valueColumn({
      field: 'financialCoverageScore',
      parentField: 'financialCoverageGroup',
      title: TRANSLATION_KEYS.score,
      getValue: ({ financialPlanCoverage }) => financialPlanCoverage.score,
      format: count,
      isScore: true,
    }),
    groupColumn('descriptionQualityGroup', TRANSLATION_KEYS.descriptionQuality),
    valueColumn({
      field: 'descriptionTotal',
      parentField: 'descriptionQualityGroup',
      title: TRANSLATION_KEYS.totalInitiatives,
      first: true,
      getValue: ({ descriptionQuality }) => descriptionQuality.totalCount,
      format: count,
    }),
    valueColumn({
      field: 'descriptionGood',
      parentField: 'descriptionQualityGroup',
      title: TRANSLATION_KEYS.wellDescribedInitiatives,
      getValue: ({ descriptionQuality }) => descriptionQuality.wellDescribedCount,
      format: count,
    }),
    valueColumn({
      field: 'descriptionScore',
      parentField: 'descriptionQualityGroup',
      title: TRANSLATION_KEYS.score,
      getValue: ({ descriptionQuality }) => descriptionQuality.score,
      format: count,
      isScore: true,
    }),
    groupColumn('strategicGrowthGroup', TRANSLATION_KEYS.strategicGrowthAreas),
    valueColumn({
      field: 'strategicTotal',
      parentField: 'strategicGrowthGroup',
      title: TRANSLATION_KEYS.totalAreas,
      first: true,
      getValue: ({ strategicGrowthAreas }) => strategicGrowthAreas.totalCount,
      format: count,
    }),
    valueColumn({
      field: 'strategicCovered',
      parentField: 'strategicGrowthGroup',
      title: TRANSLATION_KEYS.coveredAreas,
      getValue: ({ strategicGrowthAreas }) => strategicGrowthAreas.coveredCount,
      format: count,
    }),
    valueColumn({
      field: 'strategicScore',
      parentField: 'strategicGrowthGroup',
      title: TRANSLATION_KEYS.score,
      getValue: ({ strategicGrowthAreas }) => strategicGrowthAreas.score,
      format: count,
      isScore: true,
    }),
  ]
}
